import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiUrl } from '../../../utils/config';
import { useAppContext } from '../../../context';
import StarRating from '../../shared/StarRating';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button key={s} type="button" onClick={() => onChange(s)} className="text-2xl transition-transform hover:scale-110">
        <span className={s <= value ? 'text-amber-400' : 'text-gray-300'}>★</span>
      </button>
    ))}
  </div>
);

const fetchProduct = async (id) => {
  const { data } = await axios.get(`${apiUrl}/menuItem/${id}`);
  if (!data.status) throw new Error(data.error || 'Not found');
  return data.item;
};

const fetchProducts = async () => {
  const { data } = await axios.get(`${apiUrl}/menuItem`);
  return data.list || [];
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlist, customerUser } = useAppContext();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const queryClient = useQueryClient();
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reviewFeedback, setReviewFeedback] = useState(null);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ['product', id],
    queryFn: () => fetchProduct(id),
  });

  const { data: allProducts } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });

  const { data: reviewData } = useQuery({
    queryKey: ['reviews', id],
    queryFn: async () => {
      const { data } = await axios.get(`${apiUrl}/reviews/item/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const reviews = reviewData?.list || [];
  const reviewStats = reviewData?.stats || { average: 0, count: 0 };

  const isWishlisted = wishlist.some((item) => item.id === product?.id);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!customerUser) { setReviewFeedback({ type: 'error', text: 'Please log in to leave a review.' }); return; }
    if (!reviewForm.comment.trim()) { setReviewFeedback({ type: 'error', text: 'Please write a comment.' }); return; }
    setSubmitting(true);
    setReviewFeedback(null);
    try {
      const { data } = await axios.post(`${apiUrl}/reviews`, {
        menu_item_id: id,
        user_id: customerUser.id,
        user_name: customerUser.name || `${customerUser.firstName || ''} ${customerUser.lastName || ''}`.trim() || 'Customer',
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      if (data.status) {
        setReviewFeedback({ type: 'success', text: 'Review submitted! It will appear after approval.' });
        setReviewForm({ rating: 5, comment: '' });
        queryClient.invalidateQueries(['reviews', id]);
      } else {
        setReviewFeedback({ type: 'error', text: data.error || 'Failed to submit review.' });
      }
    } catch {
      setReviewFeedback({ type: 'error', text: 'Something went wrong. Please try again.' });
    }
    setSubmitting(false);
  };

  const images = product ? [
    product.images?.image,
    ...(product.images?.gallery?.map(g => g.url) || []),
  ].filter(Boolean) : [];

  const prevSlide = useCallback(() => setSlideIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const nextSlide = useCallback(() => setSlideIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, prevSlide, nextSlide]);

  const related = (allProducts || [])
    .filter((p) => p.category?.id === product?.category?.id && String(p.id) !== String(id))
    .slice(0, 4);

  const handleAddToCart = () => {
    addToCart({ ...product, qty });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-6xl mb-4">😕</p>
          <p className="text-xl font-bold text-gray-800 mb-2">Item not found</p>
          <p className="text-gray-400 text-sm mb-6">This dish may no longer be available.</p>
          <button
            onClick={() => navigate('/menu')}
            className="bg-orange-500 text-white px-6 py-3 rounded-2xl font-bold hover:bg-orange-600 transition"
          >
            Browse Menu
          </button>
        </div>
      </div>
    );
  }

  const rating = reviewStats.average || product.rating || 4.0;
  const ratingCount = reviewStats.count;
  const displayPrice = typeof product.price === 'number'
    ? `$${product.price.toFixed(2)}`
    : product.price;

  return (
    <>
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-400 mb-6 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-orange-500 transition">Home</Link>
          <span>/</span>
          <Link to="/menu" className="hover:text-orange-500 transition">Menu</Link>
          {product.category?.name && (
            <>
              <span>/</span>
              <Link to={`/category/${product.category.id}`} className="hover:text-orange-500 transition">{product.category.name}</Link>
            </>
          )}
          <span>/</span>
          <span className="text-gray-700 font-semibold truncate max-w-[160px]">{product.name}</span>
        </nav>

        {/* Main section */}
        <div className="grid md:grid-cols-2 gap-10 mb-16">
          {/* Image Slider */}
          <div className="flex flex-col gap-3">
            <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/3] bg-gray-100 group">
              {images.length > 0 ? (
                <img
                  src={images[slideIdx]}
                  alt={product.name}
                  onClick={() => setLightboxOpen(true)}
                  className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300 group-hover:scale-[1.02]"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl">🍽</div>
              )}

              {/* Zoom hint */}
              {images.length > 0 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/40 text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition pointer-events-none">
                  Click to enlarge
                </div>
              )}

              {/* Prev / Next arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition text-gray-700 text-lg"
                  >
                    ‹
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition text-gray-700 text-lg"
                  >
                    ›
                  </button>
                </>
              )}

              {/* Badges */}
              {product.veg !== undefined && (
                <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full shadow-md ${product.veg ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                  {product.veg ? '🌿 Veg' : '🍖 Non-Veg'}
                </span>
              )}
              <button
                onClick={() => toggleWishlist(product)}
                className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all text-lg
                  ${isWishlisted ? 'bg-red-500 text-white scale-110' : 'bg-white/90 text-gray-400 hover:text-red-500'}`}
              >
                {isWishlisted ? '♥' : '♡'}
              </button>

              {/* Dot indicators */}
              {images.length > 1 && (
                <div className="absolute bottom-4 right-4 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSlideIdx(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === slideIdx ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/80'}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setSlideIdx(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                      i === slideIdx ? 'border-orange-500 ring-2 ring-orange-200' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Lightbox */}
          {lightboxOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
              onClick={() => setLightboxOpen(false)}
            >
              <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
                <img
                  src={images[slideIdx]}
                  alt={product.name}
                  className="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
                />

                {/* Close */}
                <button
                  onClick={() => setLightboxOpen(false)}
                  className="absolute top-3 right-3 w-9 h-9 bg-black/60 hover:bg-black text-white rounded-full flex items-center justify-center text-lg transition"
                >
                  ×
                </button>

                {/* Lightbox arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black text-white rounded-full flex items-center justify-center text-xl transition"
                    >
                      ‹
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black text-white rounded-full flex items-center justify-center text-xl transition"
                    >
                      ›
                    </button>
                  </>
                )}

                {/* Counter */}
                {images.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">
                    {slideIdx + 1} / {images.length}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold uppercase tracking-wider text-orange-500 bg-orange-50 px-3 py-1 rounded-full inline-block mb-3 w-fit">
              {product?.category?.name || 'Uncategorized'}
            </span>

            <h1 className="font-serif text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>

            <div className="flex items-center gap-2 mb-4">
              <StarRating rating={rating} />
              <span className="text-sm text-gray-400">
                {ratingCount > 0 ? `${rating} · ${ratingCount} review${ratingCount !== 1 ? 's' : ''}` : 'No reviews yet'}
              </span>
            </div>

            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              {product.description || product.desc || 'No description available.'}
            </p>

            <div className="mb-6">
              <span className="text-4xl font-black text-orange-500">{displayPrice}</span>
            </div>

            {/* Qty + Add to Cart */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-3 py-2">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-8 h-8 rounded-xl bg-white text-gray-700 font-bold shadow-sm hover:bg-orange-50 transition"
                >
                  −
                </button>
                <span className="w-8 text-center font-bold text-gray-800">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="w-8 h-8 rounded-xl bg-white text-gray-700 font-bold shadow-sm hover:bg-orange-50 transition"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                className={`flex-1 py-3 rounded-2xl font-bold transition-all ${
                  added
                    ? 'bg-green-500 text-white scale-[0.98]'
                    : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-lg hover:scale-[1.02]'
                }`}
              >
                {added ? '✓ Added to Cart!' : '+ Add to Cart'}
              </button>
            </div>

            <button
              onClick={() => toggleWishlist(product)}
              className={`mt-3 py-2.5 rounded-2xl text-sm font-semibold border transition-all ${
                isWishlisted
                  ? 'border-red-200 bg-red-50 text-red-500'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-red-200 hover:text-red-400'
              }`}
            >
              {isWishlisted ? '♥ Saved to Wishlist' : '♡ Save to Wishlist'}
            </button>
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div>
            <h2 className="font-serif text-2xl text-gray-900 mb-5">You may also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
        {/* Reviews section */}
        <div className="mt-16">
          <h2 className="font-serif text-2xl text-gray-900 mb-6">
            Customer Reviews
            {ratingCount > 0 && (
              <span className="ml-3 text-base font-normal text-gray-400">{rating} ★ · {ratingCount} review{ratingCount !== 1 ? 's' : ''}</span>
            )}
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Review list */}
            <div className="space-y-4">
              {reviews.length === 0 ? (
                <div className="bg-gray-50 rounded-2xl p-8 text-center">
                  <p className="text-3xl mb-2">💬</p>
                  <p className="font-semibold text-gray-700 mb-1">No reviews yet</p>
                  <p className="text-sm text-gray-400">Be the first to review this dish!</p>
                </div>
              ) : (
                reviews.map((review) => (
                  <div key={review.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white text-xs font-black">
                          {(review.user_name || 'C')[0].toUpperCase()}
                        </div>
                        <span className="font-semibold text-gray-800 text-sm">{review.user_name}</span>
                      </div>
                      <StarRating rating={review.rating} />
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
                    <p className="text-xs text-gray-300 mt-2">
                      {review.created_at ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Submit review form */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 h-fit">
              <h3 className="font-bold text-gray-900 mb-4">Write a Review</h3>
              {!customerUser ? (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm mb-4">Sign in to leave a review</p>
                  <Link to="/login" className="bg-orange-500 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-orange-600 transition text-sm">
                    Login
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Your Rating</label>
                    <StarPicker value={reviewForm.rating} onChange={(v) => setReviewForm((f) => ({ ...f, rating: v }))} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Your Review</label>
                    <textarea
                      rows={4}
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                      placeholder="Share your experience with this dish..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 resize-none"
                    />
                  </div>
                  {reviewFeedback && (
                    <p className={`text-sm font-medium px-3 py-2 rounded-xl ${reviewFeedback.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                      {reviewFeedback.text}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-xl font-bold hover:shadow-md transition disabled:opacity-60"
                  >
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
